import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { projectsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import PublicHeader from '../components/PublicHeader'
import { Briefcase, Search, Filter, AlertCircle, Euro, Clock, MapPin, Eye, Users, ChevronRight, Rocket, Camera, Video, Film, Flame } from 'lucide-react'

const ProjectsPage = ({ onNavigate } = {}) => {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    category: '',
    budget: '',
    location: '',
    deadline: ''
  })
  const [showFilters, setShowFilters] = useState(false)

  const { user, isAuthenticated } = useAuth()


  useEffect(() => {
    fetchProjects()

    // Set up polling to refresh projects every 30 seconds
    const intervalId = setInterval(() => {
      fetchProjects()
    }, 30000) // 30 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId)
  }, [filters])

  const fetchProjects = async () => {
    try {
      setLoading(true)
      const params = {
        search: searchTerm,
        ...filters,
        status: 'open'
      }

      const response = await projectsAPI.getProjects(params)
      setProjects(response.data.projects)
    } catch (error) {
      console.error('Error fetching projects:', error)
      setError('Er is een fout opgetreden bij het ophalen van projecten')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    fetchProjects()
  }

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }))
  }

  const formatBudget = (budget) => {
    if (budget && budget > 0) {
      return `€${Number(budget).toLocaleString('nl-NL')}`
    }
    return 'In overleg'
  }

  const getTimeRemaining = (deadline) => {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    const diffTime = deadlineDate - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return 'Verlopen'
    if (diffDays === 0) return 'Vandaag'
    if (diffDays === 1) return 'Morgen'
    if (diffDays < 7) return `${diffDays} dagen`
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weken`
    return `${Math.ceil(diffDays / 30)} maanden`
  }

  const industryMeta = {
    fashion: { icon: '👗', label: 'Mode' },
    beauty: { icon: '💄', label: 'Beauty' },
    'food-beverage': { icon: '🍽️', label: 'Food & Beverage' },
    tech: { icon: '📱', label: 'Tech' },
    'health-wellness': { icon: '💪', label: 'Health & Wellness' },
    travel: { icon: '✈️', label: 'Reizen' },
    automotive: { icon: '🚗', label: 'Automotive' },
    'home-garden': { icon: '🏡', label: 'Wonen & Tuin' },
    sports: { icon: '⚽', label: 'Sport' },
    education: { icon: '🎓', label: 'Educatie' },
    finance: { icon: '💶', label: 'Finance' },
    entertainment: { icon: '🎬', label: 'Entertainment' },
    b2b: { icon: '💼', label: 'B2B' },
    other: { icon: '📦', label: 'Overig' }
  }

  const nicheLabels = {
    beauty: 'Beauty', fashion: 'Mode', fitness: 'Fitness', health: 'Gezondheid',
    food: 'Food', travel: 'Reizen', tech: 'Tech', gaming: 'Gaming', lifestyle: 'Lifestyle',
    parenting: 'Parenting', pets: 'Huisdieren', home: 'Wonen', diy: 'DIY',
    automotive: 'Automotive', sports: 'Sport', music: 'Muziek', art: 'Kunst',
    education: 'Educatie', business: 'Business', finance: 'Finance',
    sustainability: 'Duurzaamheid', other: 'Overig'
  }

  const getIndustryIcon = (industry) => industryMeta[industry]?.icon || '📦'

  const contentTypeMeta = {
    video: { Icon: Video, label: 'Video' },
    foto: { Icon: Camera, label: 'Foto' },
    beide: { Icon: Film, label: 'Video & Foto' }
  }

  // '9:16 (Reels/TikTok)' -> 'Reels/TikTok'
  const formatPlatform = (f) => {
    const m = typeof f === 'string' && f.match(/\(([^)]+)\)/)
    return m ? m[1] : f
  }

  const categories = [
    { value: '', label: 'Alle categorieën' },
    { value: 'beauty-cosmetics', label: 'Beauty & Cosmetica' },
    { value: 'fashion-clothing', label: 'Mode & Kleding' },
    { value: 'food-drinks', label: 'Eten & Drinken' },
    { value: 'health-fitness', label: 'Gezondheid & Fitness' },
    { value: 'technology', label: 'Technologie' },
    { value: 'travel-lifestyle', label: 'Reizen & Lifestyle' },
    { value: 'home-garden', label: 'Wonen & Tuin' },
    { value: 'business-services', label: 'Zakelijke Diensten' }
  ]

  return (
    <div className="font-['Inter'] min-h-screen bg-white text-[#0A0F0D] overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-[150%] h-[150%] bg-gradient-to-br from-[#D1FAE5]/50 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-[150%] h-[150%] bg-gradient-to-tl from-[#EDE9FE]/50 via-transparent to-transparent rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header */}
      <PublicHeader />

      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 md:mb-8">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 bg-[#ECFDF5] text-[#2A7E5F] border border-[#D1FAE5] rounded-full text-xs font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3EB489]" />
                Marktplaats
              </span>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#0A0F0D] mb-3">Ontdek opdrachten</h1>
              <p className="text-[#64748B] text-base md:text-lg">
                <span className="font-semibold text-[#1F2937]">{projects.length}</span> beschikbare opdracht{projects.length === 1 ? '' : 'en'} die bij jou passen
              </p>
            </div>

            {isAuthenticated && user?.role === 'bedrijf' && (
              <div className="mt-4 sm:mt-0">
                <button
                  onClick={() => onNavigate ? onNavigate('create-project') : window.location.href = '/projects/create'}
                  className="w-full sm:w-auto inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] hover:-translate-y-0.5 transition-all text-white shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Opdracht plaatsen
                </button>
              </div>
            )}
          </div>

          {/* Search & Filters */}
          <div className="mb-6 md:mb-8">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-4 md:mb-6">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-[#64748B] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Zoek op trefwoorden, bedrijfsnaam, of producttype..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-white border border-[#E5E7EB] rounded-2xl focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0A0F0D] placeholder-[#64748B]/70 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_4px_12px_-4px_rgba(0,0,0,0.06)] outline-none transition"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center justify-center px-5 py-3.5 rounded-2xl font-medium transition-all border ${showFilters ? 'bg-[#FAFAF7] border-[#1F2937] text-[#0A0F0D]' : 'bg-white border-[#E5E7EB] text-[#1F2937] hover:border-[#1F2937]'}`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-[#3EB489] rounded-2xl font-semibold hover:bg-[#359E78] hover:-translate-y-0.5 transition-all text-white shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]"
              >
                Zoeken
              </button>
            </form>

            {/* Filters Panel */}
            {showFilters && (
              <div className="relative mb-6">
                <div className="relative p-4 md:p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.08)]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Categorie
                      </label>
                      <select
                        value={filters.category}
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A]"
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Budget
                      </label>
                      <select
                        value={filters.budget}
                        onChange={(e) => handleFilterChange('budget', e.target.value)}
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A]"
                      >
                        <option value="">Alle budgetten</option>
                        <option value="0-250">€0 - €250</option>
                        <option value="250-500">€250 - €500</option>
                        <option value="500-1000">€500 - €1.000</option>
                        <option value="1000-2500">€1.000 - €2.500</option>
                        <option value="2500+">€2.500+</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Locatie
                      </label>
                      <input
                        type="text"
                        placeholder="Stad of regio"
                        value={filters.location}
                        onChange={(e) => handleFilterChange('location', e.target.value)}
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#475569]/50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Deadline
                      </label>
                      <select
                        value={filters.deadline}
                        onChange={(e) => handleFilterChange('deadline', e.target.value)}
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A]"
                      >
                        <option value="">Alle deadlines</option>
                        <option value="today">Vandaag</option>
                        <option value="week">Deze week</option>
                        <option value="month">Deze maand</option>
                        <option value="3months">Komende 3 maanden</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-3 md:px-6 pb-8 md:pb-16">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-500">{error}</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#ECFDF5] flex items-center justify-center">
              <Briefcase className="w-10 h-10 text-[#3EB489]" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A0F0D] mb-2">Geen opdrachten gevonden</h3>
            <p className="text-[#64748B] mb-8 max-w-md mx-auto">
              Probeer je zoekopdracht aan te passen of de filters te wijzigen.
            </p>
            {isAuthenticated && user?.role === 'bedrijf' && (
              <button onClick={() => onNavigate ? onNavigate('create-project') : window.location.href = '/projects/create'} className="inline-flex items-center px-6 py-3 bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] transition text-white shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]">
                Plaats de eerste opdracht
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
            {projects.map((project) => {
              const ct = contentTypeMeta[project.contentType]
              const niches = project.desiredCreatorProfile?.niches || []
              const cities = project.desiredCreatorProfile?.location?.locationImportant
                ? (project.desiredCreatorProfile?.location?.cities || [])
                : []
              const isExpired = typeof project.daysUntilDeadline === 'number' && project.daysUntilDeadline < 0
              const isUrgent = project.urgency === 'high' || project.urgency === 'urgent'
              return (
              <div key={project.id || project._id} className="group relative min-w-0 h-full">
                <Link
                  to={`/projects/${project.id || project._id}`}
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('project-detail', project.id || project._id) } }}
                  className="h-full flex flex-col rounded-[22px] bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_24px_48px_-24px_rgba(0,0,0,0.18)] hover:-translate-y-1 hover:border-[#D1FAE5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3EB489] transition-all duration-300 overflow-hidden cursor-pointer text-inherit no-underline"
                >
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    {/* Company row */}
                    <div className="flex items-center gap-3 mb-4">
                      {project.owner?.logoUrl ? (
                        <img
                          src={project.owner.logoUrl}
                          alt={project.owner.companyName || 'Bedrijf'}
                          className="w-11 h-11 rounded-[14px] object-cover border border-[#E5E7EB] bg-white flex-shrink-0"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div
                        className="w-11 h-11 rounded-[14px] flex items-center justify-center text-xl font-bold bg-[#FAFAF7] text-[#2A7E5F] border border-[#E5E7EB] flex-shrink-0"
                        style={project.owner?.logoUrl ? { display: 'none' } : {}}
                      >
                        {project.owner?.companyName ? project.owner.companyName.charAt(0).toUpperCase() : getIndustryIcon(project.industry)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#1F2937] truncate">
                          {project.owner?.companyName || 'Onbekend bedrijf'}
                        </p>
                        <p className="text-xs text-[#64748B]">
                          {project.publishedAt && !isNaN(new Date(project.publishedAt))
                            ? `Geplaatst ${new Date(project.publishedAt).toLocaleDateString('nl-NL')}`
                            : 'Nieuw geplaatst'}
                        </p>
                      </div>
                      {isUrgent && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#FFF7ED] text-[#C2410C] border border-[#FFEDD5] flex-shrink-0">
                          <Flame className="w-3 h-3" /> Spoed
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-[#0A0F0D] leading-snug mb-3 line-clamp-2 group-hover:text-[#2A7E5F] transition-colors">
                      {project.title}
                    </h3>

                    {/* Badges: content type + platforms + industry */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                      {ct && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#2A7E5F] border border-[#D1FAE5]">
                          <ct.Icon className="w-3.5 h-3.5" /> {ct.label}
                        </span>
                      )}
                      {(project.contentFormat || []).slice(0, 2).map((f, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#F5F3FF] text-[#7C3AED] border border-[#EDE9FE]">
                          {formatPlatform(f)}
                        </span>
                      ))}
                      {project.industry && industryMeta[project.industry] && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#FAFAF7] text-[#1F2937] border border-[#E5E7EB]">
                          {industryMeta[project.industry].icon} {industryMeta[project.industry].label}
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-[#475569] text-sm leading-relaxed mb-4 line-clamp-2">
                      {project.description}
                    </p>

                    {/* Niches */}
                    {niches.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {niches.slice(0, 3).map((n, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-[#F1F3F5] text-[#64748B]">
                            #{nicheLabels[n] || n}
                          </span>
                        ))}
                        {niches.length > 3 && (
                          <span className="text-xs text-[#64748B] self-center">+{niches.length - 3}</span>
                        )}
                      </div>
                    )}

                    {cities.length > 0 && (
                      <div className="flex items-center text-sm text-[#64748B] min-w-0 mb-4">
                        <MapPin className="w-4 h-4 mr-1.5 text-[#7C3AED] flex-shrink-0" />
                        <span className="truncate">{cities.join(', ')}</span>
                      </div>
                    )}

                    {/* Footer: budget + deadline */}
                    <div className="mt-auto pt-4 border-t border-[#F1F3F5]">
                      <div className="flex items-end justify-between gap-3 mb-4">
                        <div className="min-w-0">
                          <p className="text-xs text-[#64748B] mb-0.5">Budget</p>
                          <p className="text-xl font-black text-[#0A0F0D] leading-none truncate">
                            {formatBudget(project.budget)}
                          </p>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 ${isExpired ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-[#ECFDF5] text-[#2A7E5F] border border-[#D1FAE5]'}`}>
                          <Clock className="w-3.5 h-3.5" />
                          {getTimeRemaining(project.deadline)}
                        </span>
                      </div>

                      {/* Stats + CTA */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-xs text-[#64748B]">
                          <span className="inline-flex items-center" title="Weergaven">
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            {project.stats?.views || 0}
                          </span>
                          <span className="inline-flex items-center" title="Voorstellen">
                            <Users className="w-3.5 h-3.5 mr-1" />
                            {project.stats?.proposalsCount || 0}
                          </span>
                        </div>
                        <span className="inline-flex items-center text-sm font-semibold text-[#0A0F0D] group-hover:text-[#2A7E5F] transition-colors">
                          Bekijk details
                          <ChevronRight className="w-4 h-4 ml-0.5 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Call to Action for non-authenticated users */}
      {!isAuthenticated && (
        <div className="relative mt-16 mb-8">
          <div className="relative max-w-4xl mx-auto text-center px-6 py-16 rounded-3xl bg-gradient-to-b from-white to-[#FAFAF7] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.03),0_24px_50px_-28px_rgba(0,0,0,0.12)]">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#3EB489] to-[#359E78] flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(62,180,137,0.6)]">
              <Rocket className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-black mb-4 text-[#0A0F0D]">
              Klaar om te starten?
            </h2>
            <p className="text-lg text-[#64748B] mb-8 max-w-2xl mx-auto">
              Meld je aan en begin vandaag nog met het vinden van toffe opdrachten of plaats je eerste project
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-[#3EB489] rounded-2xl font-semibold hover:bg-[#359E78] hover:-translate-y-0.5 transition-all text-lg text-white shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]"
              >
                Aanmelden als Creator
              </Link>
              <Link
                to="/register"
                className="px-8 py-4 bg-white border border-[#E5E7EB] rounded-2xl font-semibold hover:border-[#0A0F0D] hover:bg-[#FAFAF7] transition text-lg text-[#0A0F0D]"
              >
                Opdracht Plaatsen
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectsPage